import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { BarItemProps, ResponsiveBar } from '@nivo/bar';
import { AxisTickProps } from '@nivo/axes';

import { useMediaQuery } from 'react-responsive';
import { CaptionPositionData, DataFormated } from '../types';

import { makeRangeArray } from '../../../../../utils/functions';

import {
  Container,
  AverageContainer,
  ContainerBar,
  WrapBar,
  WrapContent,
  WrapLegendAndContainer,
  Legend,
  LegendDateType,
  AverageBox,
  AverageBoxHeader,
  AverageBoxValue,
  AverageBoxPeriod,
  AverageBoxTextHeader,
  AverageBoxTextValue,
  AverageBoxTextValueUnit,
  AverageBoxPeriodText,
  AverageBoxValueWrap,
} from './styles';
import { filterProvider } from '../providers';

type ContentProps = {
  data: DataFormated[];
  average: string | number;
  maxValue: number;
  dataValue: number;
};

const Content: React.FC<ContentProps> = ({
  data,
  average,
  maxValue,
  dataValue,
}) => {
  const [detailsBar, setDetailsBar] = useState({
    id: 0,
    value: 0,
  });
  const [rangeValues, setRangeValues] = useState([
    0, 50, 100, 150, 200, 250, 300,
  ]);

  const [captionPosition, setCaptionPosition] = useState<CaptionPositionData[]>(
    []
  );

  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    const rangeMaked = makeRangeArray(maxValue);
    setRangeValues(rangeMaked);
  }, [maxValue]);

  const handleClickDetails = useCallback(
    // @ts-ignore
    (barIndexValue, value) => {
      if (detailsBar.id === barIndexValue) {
        setDetailsBar({
          id: 0,
          value: 0,
        });
      } else {
        setDetailsBar({
          id: barIndexValue,
          value,
        });
      }
    },
    [detailsBar]
  );

  const CustomBarComponent = useCallback(
    ({ bar }: BarItemProps<DataFormated>): JSX.Element => {
      const barIndexValue = bar.data.indexValue;

      const isInactive =
        detailsBar.id !== barIndexValue && detailsBar.value > 0;

      const idxItemBar = captionPosition.findIndex(
        (cap) => cap.key === barIndexValue
      );
      const positionCalculate =
        captionPosition[idxItemBar]?.value - (isMobile ? 7 : 14);

      return (
        <g
          transform={`translate(${positionCalculate}, ${bar.y})`}
          className="custom-bar-component"
          onClick={() => handleClickDetails(barIndexValue, bar.data.value)}
        >
          <g
            id={`tooltip-${bar.key}`}
            transform={`translate(${isMobile ? '-12' : '-6'}, -42)`}
          >
            <text
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fill: '#333333',
                fontSize: isMobile ? 10 : 12,
                fontWeight: 'bold',
              }}
              x="22"
              y="30"
            >
              {`${bar.data.value}Kg`}
            </text>
          </g>
          <rect
            className={`bar-item ${isInactive ? 'inactive' : ''}`}
            width={isMobile ? 16 : 29}
            rx={isMobile ? '5' : '8'}
            height={bar.height + (isMobile ? 5 : 8)}
            fill="#0B29FB"
            strokeWidth="0"
            stroke="#0B29FB"
          />
        </g>
      );
    },
    [captionPosition, handleClickDetails, detailsBar, isMobile]
  );

  const CustomTick = useCallback(
    (tick: AxisTickProps<string>): JSX.Element => {
      const { value, x, y } = tick;
      const tickValue = value;
      const tickX = x;

      const isActive = String(detailsBar.id) === value;

      const newItem: CaptionPositionData = {
        key: tickValue,
        value: tickX,
      };

      const newValue = captionPosition;

      const idxItem = newValue.findIndex((val) => val.key === tickValue);

      if (idxItem === -1) {
        newValue.push(newItem);
      } else {
        newValue[idxItem] = newItem;
      }

      if (newValue.length > 0) {
        setCaptionPosition(newValue);
      }

      const valueSplit = value.split('/');

      return (
        <g transform={`translate(${x},${y + 22})`}>
          <line stroke="#777777" strokeWidth={1.5} y1={-1} y2={-13} />
          {valueSplit.length === 3 ? (
            <text
              y={16}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fill: 'rgb(129, 129, 129)',
                fontSize: 12,
                fontWeight: isActive ? 700 : 400,
              }}
            >
              {value}
            </text>
          ) : (
            <>
              <text
                y={8}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: 'rgb(129, 129, 129)',
                  fontSize: 10,
                  fontWeight: isActive ? 700 : 400,
                }}
              >
                {valueSplit[0]}
              </text>
              <text
                y={18}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: 'rgb(129, 129, 129)',
                  fontSize: 10,
                  fontWeight: isActive ? 700 : 400,
                }}
              >
                {valueSplit[1]}
              </text>
            </>
          )}
        </g>
      );
    },
    [captionPosition, detailsBar]
  );

  const avaregeDetail = useMemo(() => {
    return detailsBar.value ? detailsBar.value : average;
  }, [average, detailsBar]);

  const monthDetail = useMemo(() => {
    if (filterProvider.length === 0) return '';
    const monthSelected = filterProvider.find(
      (filter) => filter.value === dataValue
    );

    return detailsBar.value > 0 ? detailsBar.id : monthSelected?.label || '';
  }, [detailsBar, dataValue]);

  return (
    <Container id="graph">
      <WrapLegendAndContainer>
        <Legend>Volume(kg)</Legend>
        <ContainerBar id="componenteGrafico">
          <WrapBar>
            <WrapContent>
              <ResponsiveBar
                markers={[
                  {
                    axis: 'y',
                    value: 0,
                    lineStyle: {
                      stroke: '#DDDDDD',
                    },
                  },
                  {
                    axis: 'x',
                    value: 0,
                    lineStyle: {
                      stroke: '#DDDDDD',
                    },
                  },
                ]}
                isInteractive
                enableGridY={false}
                enableGridX={false}
                borderColor="red"
                data={data}
                keys={['value']}
                indexBy="data"
                layout="vertical"
                margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                padding={0.3}
                animate={false}
                axisTop={null}
                axisRight={null}
                groupMode="grouped"
                gridYValues={rangeValues}
                axisBottom={{
                  renderTick: CustomTick,
                  tickSize: 8,
                  tickPadding: 5,
                }}
                axisLeft={{
                  tickSize: 8,
                  tickPadding: 2,
                  format: (value) => `${value}`,
                  tickValues: rangeValues,
                }}
                maxValue={rangeValues[5]}
                minValue="auto"
                barComponent={CustomBarComponent}
              />
            </WrapContent>
          </WrapBar>
        </ContainerBar>
        <LegendDateType>{dataValue > 1 && 'Mês/Ano'}</LegendDateType>
      </WrapLegendAndContainer>
      <AverageContainer>
        <AverageBox>
          <AverageBoxHeader>
            <AverageBoxTextHeader>Volume</AverageBoxTextHeader>
          </AverageBoxHeader>
          <AverageBoxValue>
            <AverageBoxValueWrap>
              <AverageBoxTextValue>
                {Number(avaregeDetail).toFixed(0)}
              </AverageBoxTextValue>
              <AverageBoxTextValueUnit>kg</AverageBoxTextValueUnit>
            </AverageBoxValueWrap>
          </AverageBoxValue>
          <AverageBoxPeriod>
            <AverageBoxPeriodText>{monthDetail}</AverageBoxPeriodText>
          </AverageBoxPeriod>
        </AverageBox>
      </AverageContainer>
    </Container>
  );
};

export default Content;
